import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

export function SpacebarMasher({ onScore }) {
  const [pos, setPos] = useState(50); // 50 = center; >100 you win, <0 lose
  const [time, setTime] = useState(10);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const reported = useRef(false);

  const start = () => { setPos(50); setTime(10); setRunning(true); setResult(null); reported.current = false; };

  const mash = useCallback(() => { if (running) setPos((p) => Math.min(100, p + 3)); }, [running]);

  useEffect(() => {
    if (!running) return;
    const drift = setInterval(() => setPos((p) => Math.max(0, p - 1.2)), 120);
    return () => clearInterval(drift);
  }, [running]);

  useEffect(() => {
    if (!running) return;
    if (time <= 0) { setRunning(false); setResult(pos >= 80 ? "win" : "lose"); return; }
    const t = setTimeout(() => setTime((x) => x - 1), 1000);
    return () => clearTimeout(t);
  }, [running, time, pos]);

  useEffect(() => {
    if (running && pos >= 100) { setRunning(false); setResult("win"); }
  }, [pos, running]);

  useEffect(() => {
    const key = (e) => { if (e.code === "Space") { e.preventDefault(); mash(); } };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [mash]);

  useEffect(() => { if (result && !reported.current) { reported.current = true; onScore?.(result === "win" ? Math.round(pos) * 20 + 500 : Math.round(pos) * 10); } }, [result, pos, onScore]);

  return (
    <div className="flex flex-col items-center w-full max-w-md text-center" data-testid="spacebar-masher">
      <p className="font-mono text-neon-yellow text-lg mb-4">⏱ {time}s</p>
      <div className="w-full h-10 bg-zinc-800 rounded-full overflow-hidden border-2 border-zinc-700 mb-6">
        <div className="h-full transition-all duration-75" data-testid="masher-bar" style={{ width: `${pos}%`, background: pos >= 80 ? "#39FF14" : "#00F0FF" }} />
      </div>
      {!running ? (
        <>
          {result && <p className={`font-display font-black text-2xl uppercase mb-3 ${result === "win" ? "text-neon-lime" : "text-neon-pink"}`} data-testid="masher-result">{result === "win" ? "You filled it! 💪" : "Not enough! Try harder"}</p>}
          <Button onClick={start} data-testid="masher-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase h-12 px-8">
            <Play className="w-4 h-4 mr-2 fill-black" /> {result ? "Play Again" : "Start"}
          </Button>
        </>
      ) : (
        <button onClick={mash} data-testid="masher-tap" className="w-full py-8 rounded-xl bg-neon-pink text-black font-display font-black text-2xl uppercase active:scale-95 transition-transform">
          MASH! (or Spacebar)
        </button>
      )}
      <p className="text-zinc-500 text-xs font-mono mt-4">Mash Space (or the button) to fill the bar before time runs out!</p>
    </div>
  );
}
